(() => {
  if (window.__quickAddPdpModalEnhancer) return;

  class QuickAddPdpModalEnhancer {
    constructor() {
      this.enhanceDelays = [0, 80, 250, 700];
      this.kachingRefreshDelay = 120;
      this.scrollIdleDelay = 220;
      this.scrollIdleTimers = new WeakMap();
      this.kachingRootSelector = 'kaching-bundle, kaching-bundles, [id*="kaching"]';
      this.kachingMethodNames = [
        'refresh',
        'reload',
        'render',
        'mount',
        'init',
        'initialize',
        'hydrate',
        'load'
      ];

      this.watchQuickAddContent = this.watchQuickAddContent.bind(this);
      this.refreshOpenReadMoreFeatures = this.refreshOpenReadMoreFeatures.bind(this);
    }

    init() {
      this.patchQuickAddModal();
      this.waitForQuickAddMarkup();
      window.addEventListener('resize', this.refreshOpenReadMoreFeatures);
    }

    patchQuickAddModal() {
      customElements.whenDefined('quick-add-modal').then(() => {
        const QuickAddModal = customElements.get('quick-add-modal');
        if (!QuickAddModal || QuickAddModal.prototype.__quickAddPdpModalPatched) return;

        const originalSetInnerHTML = QuickAddModal.prototype.setInnerHTML;
        if (typeof originalSetInnerHTML !== 'function') return;

        // Quick add fetches product HTML after click. Run our PDP modal setup
        // right after that HTML is placed inside the modal.
        QuickAddModal.prototype.setInnerHTML = (function(enhancer) {
          return function(element, html) {
            originalSetInnerHTML.call(this, element, html);
            enhancer.prepareNewInjection(element);
            enhancer.queueEnhancement(this, element);
          };
        })(this);

        QuickAddModal.prototype.__quickAddPdpModalPatched = true;
      });
    }

    waitForQuickAddMarkup() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.watchQuickAddContent);
        return;
      }

      this.watchQuickAddContent();
    }

    watchQuickAddContent() {
      document.querySelectorAll('quick-add-modal [id^="QuickAddInfo-"]').forEach((content) => {
        if (content.dataset.quickAddPdpObserved === 'true') return;
        content.dataset.quickAddPdpObserved = 'true';

        new MutationObserver((mutations) => {
          const hasNewHtml = mutations.some((mutation) => mutation.addedNodes.length);
          const modal = content.closest('quick-add-modal');
          if (!hasNewHtml || !modal || !content.children.length) return;

          if (this.hasProductMarkup(mutations)) {
            this.prepareNewInjection(content);
          }

          this.queueEnhancement(modal, content);
        }).observe(content, { childList: true });
      });
    }

    hasProductMarkup(mutations) {
      return mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return false;
          return node.matches('product-info, .product') || node.querySelector('product-info, .product');
        });
      });
    }

    prepareNewInjection(content) {
      if (!content) return;

      content.dataset.quickAddPdpInjectionId = `${Date.now()}-${Math.random()}`;
      delete content.dataset.quickAddPdpDynamicAnnounced;
      delete content.dataset.quickAddPdpKachingMountedAnnounced;
      delete content.dataset.quickAddPdpKachingRefreshScheduled;
    }

    queueEnhancement(modal, content) {
      if (!modal || !content) return;

      modal.classList.add('quick-add-modal--pdp');
      this.bindModalScrollGuard(modal);

      // Theme scripts and app blocks can finish at different times after AJAX.
      // A few light retries keep gallery and read-more in sync.
      requestAnimationFrame(() => this.enhanceModal(modal, content));
      this.enhanceDelays.forEach((delay) => {
        window.setTimeout(() => this.enhanceModal(modal, content), delay);
      });
    }

    enhanceModal(modal, content) {
      this.setupGallery(content);
      this.setupReadMoreFeatures(content);
      this.setupKachingBundles(modal, content);

      if (content.dataset.quickAddPdpDynamicAnnounced === 'true') return;

      content.dataset.quickAddPdpDynamicAnnounced = 'true';
      this.announceDynamicContent(modal, content);
    }

    setupGallery(scope) {
      scope.querySelectorAll('media-gallery').forEach((gallery) => {
        const activeMediaId = this.ensureGalleryHasActiveMedia(gallery);

        if (typeof gallery.setActiveMedia === 'function') {
          this.syncThemeGallery(gallery, activeMediaId);
          return;
        }

        this.bindFallbackThumbnails(gallery);
      });
    }

    ensureGalleryHasActiveMedia(gallery) {
      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      if (!viewer) return null;

      const activeMedia = viewer.querySelector('[data-media-id].is-active');
      if (activeMedia) {
        this.ensureActiveThumbnail(gallery, activeMedia.dataset.mediaId);
        return activeMedia.dataset.mediaId;
      }

      const firstMedia = viewer.querySelector('[data-media-id]');
      if (!firstMedia) return null;

      firstMedia.classList.add('is-active');
      this.ensureActiveThumbnail(gallery, firstMedia.dataset.mediaId);
      return firstMedia.dataset.mediaId;
    }

    syncThemeGallery(gallery, mediaId) {
      if (!mediaId || gallery.dataset.quickAddPdpGalleryReady === 'true') return;

      gallery.dataset.quickAddPdpGalleryReady = 'true';
      requestAnimationFrame(() => gallery.setActiveMedia(mediaId, false));
    }

    ensureActiveThumbnail(gallery, mediaId) {
      if (!mediaId) return;

      const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (!thumbnails || thumbnails.querySelector('button[aria-current]')) return;

      const thumbnail = thumbnails.querySelector(`[data-target="${mediaId}"]`);
      const button = thumbnail && thumbnail.querySelector('button');
      if (button) button.setAttribute('aria-current', 'true');
    }

    bindFallbackThumbnails(gallery) {
      const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
      if (!thumbnails) return;

      thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
        const button = thumbnail.querySelector('button');
        if (!button || button.dataset.quickAddPdpThumbnailBound === 'true') return;

        button.dataset.quickAddPdpThumbnailBound = 'true';
        button.addEventListener('click', () => {
          this.showMediaWithoutThemeGallery(gallery, thumbnail.dataset.target, button);
        });
      });
    }

    showMediaWithoutThemeGallery(gallery, mediaId, activeButton) {
      if (!mediaId) return;

      const viewer = gallery.querySelector('[id^="GalleryViewer"]');
      const thumbnails = gallery.querySelector('[id^="GalleryThumbnails"]');
      const activeMedia = viewer && viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;

      viewer.querySelectorAll('[data-media-id]').forEach((media) => media.classList.remove('is-active'));
      activeMedia.classList.add('is-active');

      if (window.pauseAllMedia) window.pauseAllMedia();

      const deferredMedia = activeMedia.querySelector('.deferred-media');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent(false);
      }

      activeMedia.parentElement.scrollTo({
        left: activeMedia.offsetLeft,
        behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
      });

      if (!thumbnails) return;

      thumbnails.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));
      if (activeButton) activeButton.setAttribute('aria-current', 'true');
      if (activeButton && activeButton.scrollIntoView) {
        activeButton.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    setupReadMoreFeatures(scope) {
      scope.querySelectorAll('[data-affirmation-read-more]').forEach((root) => {
        if (root.dataset.quickAddPdpReadMoreReady === 'true') return;

        const button = root.querySelector('[data-affirmation-toggle]');
        const content = root.querySelector('[data-affirmation-content]');
        if (!button || !content) return;

        root.dataset.quickAddPdpReadMoreReady = 'true';
        this.setReadMoreOpen(root, button, content, false);

        button.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setReadMoreOpen(root, button, content, !root.classList.contains('is-open'));
        }, true);

        content.addEventListener('transitionend', (event) => {
          if (event.propertyName === 'max-height') {
            this.refreshReadMoreHeight(root, content);
          }
        });

        content.querySelectorAll('img').forEach((image) => {
          if (!image.complete) {
            image.addEventListener('load', () => this.refreshReadMoreHeight(root, content), { once: true });
          }
        });
      });
    }

    setReadMoreOpen(root, button, content, isOpen) {
      root.classList.toggle('is-open', isOpen);
      button.setAttribute('aria-expanded', String(isOpen));
      content.setAttribute('aria-hidden', String(!isOpen));

      if (this.prefersReducedMotion()) {
        content.style.maxHeight = isOpen ? 'none' : '0px';
        return;
      }

      if (isOpen) {
        content.style.maxHeight = '0px';
        requestAnimationFrame(() => {
          content.style.maxHeight = `${content.scrollHeight}px`;
        });
        return;
      }

      content.style.maxHeight = `${content.scrollHeight}px`;
      requestAnimationFrame(() => {
        content.style.maxHeight = '0px';
      });
    }

    refreshReadMoreHeight(root, content) {
      if (!root || !content || !root.classList.contains('is-open')) return;

      content.style.maxHeight = this.prefersReducedMotion()
        ? 'none'
        : `${content.scrollHeight}px`;
    }

    refreshOpenReadMoreFeatures() {
      document.querySelectorAll('quick-add-modal [data-affirmation-read-more].is-open').forEach((root) => {
        this.refreshReadMoreHeight(root, root.querySelector('[data-affirmation-content]'));
      });
    }

    setupKachingBundles(modal, content) {
      const productInfo = content.querySelector('product-info');
      const productForm = content.querySelector('product-form, form[action*="/cart/add"]');
      const kachingRoots = this.getKachingRoots(content);
      const hasKaching = kachingRoots.length > 0;

      modal.classList.toggle('quick-add-modal--has-kaching', hasKaching);
      content.classList.toggle('quick-add-modal__content-info--has-kaching', hasKaching);
      if (productInfo) productInfo.classList.toggle('quick-add-product-info--has-kaching', hasKaching);

      kachingRoots.forEach((root) => {
        root.classList.add('quick-add-kaching');
        root.setAttribute('data-quick-add-kaching', 'true');
      });

      if (productInfo) {
        content.dataset.quickAddPdpSectionId = productInfo.dataset.section || '';
        content.dataset.quickAddPdpOriginalSectionId = productInfo.dataset.originalSection || productInfo.dataset.section || '';
      }

      if (productForm) {
        productForm.setAttribute('data-quick-add-product-form', 'true');
      }

      this.watchKachingMounts(modal, content);
    }

    watchKachingMounts(modal, content) {
      if (!modal || !content || content.dataset.quickAddPdpKachingObserved === 'true') return;
      content.dataset.quickAddPdpKachingObserved = 'true';

      new MutationObserver((mutations) => {
        const hasKachingNode = mutations.some((mutation) => {
          return Array.from(mutation.addedNodes).some((node) => {
            if (node.nodeType !== Node.ELEMENT_NODE) return false;
            return node.matches(this.kachingRootSelector) || node.querySelector(this.kachingRootSelector);
          });
        });

        if (!hasKachingNode) return;

        this.setupKachingBundles(modal, content);

        if (content.dataset.quickAddPdpKachingMountedAnnounced === 'true') return;

        content.dataset.quickAddPdpKachingMountedAnnounced = 'true';
        this.announceDynamicContent(modal, content, { forceKachingRefresh: true });
      }).observe(content, { childList: true, subtree: true });
    }

    getKachingRoots(scope) {
      if (!scope) return [];

      const roots = Array.from(scope.querySelectorAll(this.kachingRootSelector)).filter((element) => {
        return this.isKachingRoot(element);
      });

      return [...new Set(roots)];
    }

    isKachingRoot(element) {
      if (!element || ['SCRIPT', 'STYLE', 'TEMPLATE'].includes(element.tagName)) return false;

      const tagName = (element.localName || '').toLowerCase();
      const id = (element.id || '').toLowerCase();

      return tagName === 'kaching-bundle' || tagName === 'kaching-bundles' || id.includes('kaching');
    }

    announceDynamicContent(modal, content, options = {}) {
      const productInfo = content.querySelector('product-info');
      const detail = this.buildDynamicContentDetail(modal, content, productInfo);

      content.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('quick-add:pdp-modal:loaded', { detail }));

      [
        'kaching:refresh',
        'kaching-bundles:refresh',
        'kaching-bundles:initialize',
        'kaching-bundles:section:load',
        'kaching-bundles:quick-add:load'
      ].forEach((eventName) => {
        [document, modal, content, productInfo].filter(Boolean).forEach((target) => {
          target.dispatchEvent(new CustomEvent(eventName, { detail }));
        });
      });

      this.scheduleKachingRefresh(content, detail, options);
    }

    buildDynamicContentDetail(modal, content, productInfo) {
      return {
        modal,
        content,
        productInfo,
        productForm: content.querySelector('product-form, form[action*="/cart/add"]'),
        kachingRoots: this.getKachingRoots(content),
        sectionId: productInfo ? productInfo.dataset.section : content.dataset.quickAddPdpSectionId,
        originalSectionId: productInfo
          ? productInfo.dataset.originalSection || productInfo.dataset.section
          : content.dataset.quickAddPdpOriginalSectionId
      };
    }

    scheduleKachingRefresh(scope, detail, options = {}) {
      if (!scope) return;

      const isForcedRefresh = options.forceKachingRefresh === true;
      if (!isForcedRefresh && scope.dataset.quickAddPdpKachingRefreshScheduled === 'true') return;

      const injectionId = scope.dataset.quickAddPdpInjectionId || '';
      scope.dataset.quickAddPdpKachingRefreshScheduled = 'true';

      window.setTimeout(() => {
        if (injectionId && scope.dataset.quickAddPdpInjectionId !== injectionId) return;
        this.refreshKachingWhenIdle(scope, detail, injectionId);
      }, this.kachingRefreshDelay);
    }

    refreshKachingWhenIdle(scope, detail, injectionId) {
      if (injectionId && scope.dataset.quickAddPdpInjectionId !== injectionId) return;

      if (this.isUserScrolling(scope)) {
        window.setTimeout(() => this.refreshKachingWhenIdle(scope, detail, injectionId), this.scrollIdleDelay);
        return;
      }

      this.preserveModalScroll(scope, () => this.refreshKachingBundles(scope, detail));
    }

    refreshKachingBundles(scope, detail = {}) {
      const kachingRoots = this.getKachingRoots(scope);
      const refreshDetail = {
        ...detail,
        kachingRoots: kachingRoots.length ? kachingRoots : detail.kachingRoots || []
      };
      const targets = [scope, ...refreshDetail.kachingRoots].filter(Boolean);

      // Kaching is loaded by an app script, and its public API can vary by
      // version. Try the known app hooks without letting failures stop the page.
      this.tryKachingFunction(window.kachingBundlesInitialize, targets, refreshDetail);

      [
        window.kachingBundlesApi,
        window.KachingBundles,
        window.kachingBundles,
        window.KachingBundle,
        window.kachingBundle
      ].filter(Boolean).forEach((api) => {
        if (typeof api === 'function') {
          this.tryKachingFunction(api, targets, refreshDetail);
          return;
        }

        this.getKachingMethodNames(api).forEach((method) => {
          if (typeof api[method] === 'function') {
            this.tryKachingFunction(api[method].bind(api), targets, refreshDetail);
          }
        });
      });

      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
    }

    getKachingMethodNames(api) {
      const methodNames = new Set(this.kachingMethodNames);

      Object.keys(api || {}).forEach((method) => {
        if (/refresh|reload|render|mount|init|hydrate|load|update/i.test(method)) {
          methodNames.add(method);
        }
      });

      return [...methodNames];
    }

    tryKachingFunction(callback, targets, detail) {
      if (typeof callback !== 'function') return false;

      const target = targets[0] || document;
      const options = {
        root: target,
        scope: target,
        container: target,
        modal: detail.modal,
        content: detail.content,
        productInfo: detail.productInfo,
        productForm: detail.productForm,
        kachingRoots: detail.kachingRoots,
        detail
      };
      const attempts = callback.length === 0
        ? [
            () => callback(),
            () => callback(options),
            () => callback(target)
          ]
        : [
            () => callback(options),
            () => callback(target),
            () => callback()
          ];

      return attempts.some((attempt) => {
        try {
          attempt();
          return true;
        } catch (error) {
          return false;
        }
      });
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    bindModalScrollGuard(modal) {
      const scroller = this.getModalScroller(modal);
      if (!scroller || scroller.dataset.quickAddPdpScrollGuard === 'true') return;

      scroller.dataset.quickAddPdpScrollGuard = 'true';
      ['wheel', 'touchmove'].forEach((eventName) => {
        scroller.addEventListener(eventName, () => this.markUserScrolling(scroller), { passive: true });
      });
    }

    markUserScrolling(scroller) {
      if (!scroller) return;

      scroller.dataset.quickAddPdpUserScrolling = 'true';
      window.clearTimeout(this.scrollIdleTimers.get(scroller));

      this.scrollIdleTimers.set(
        scroller,
        window.setTimeout(() => {
          delete scroller.dataset.quickAddPdpUserScrolling;
        }, this.scrollIdleDelay)
      );
    }

    isUserScrolling(scope) {
      const scroller = this.getModalScroller(scope);
      return scroller && scroller.dataset.quickAddPdpUserScrolling === 'true';
    }

    preserveModalScroll(scope, callback) {
      const scroller = this.getModalScroller(scope);
      const scrollTop = scroller ? scroller.scrollTop : null;

      callback();

      if (!scroller || scrollTop === null) return;

      requestAnimationFrame(() => {
        if (this.isUserScrolling(scope)) return;
        scroller.scrollTop = scrollTop;
      });
    }

    getModalScroller(scope) {
      if (!scope || !scope.closest) return null;

      const modal = scope.matches('quick-add-modal') ? scope : scope.closest('quick-add-modal');
      return modal ? modal.querySelector('.quick-add-modal__content') : null;
    }

    getKachingMethodNames(api) {
      const methodNames = new Set(this.kachingMethodNames);

      Object.keys(api || {}).forEach((method) => {
        if (/refresh|reload|render|mount|init|hydrate|load|update/i.test(method)) {
          methodNames.add(method);
        }
      });

      return [...methodNames];
    }

    tryKachingFunction(callback, targets, detail) {
      if (typeof callback !== 'function') return false;

      const target = targets[0] || document;
      const options = {
        root: target,
        scope: target,
        container: target,
        modal: detail.modal,
        content: detail.content,
        productInfo: detail.productInfo,
        productForm: detail.productForm,
        kachingRoots: detail.kachingRoots,
        detail
      };
      const attempts = callback.length === 0
        ? [
            () => callback(),
            () => callback(options),
            () => callback(target)
          ]
        : [
            () => callback(options),
            () => callback(target),
            () => callback()
          ];

      return attempts.some((attempt) => {
        try {
          attempt();
          return true;
        } catch (error) {
          return false;
        }
      });
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  window.__quickAddPdpModalEnhancer = new QuickAddPdpModalEnhancer();
  window.__quickAddPdpModalEnhancer.init();
})();
